// AppLock — lock screen, PIN setup, privacy cover, and the LockGate
// orchestrator that App.js mounts. iOS native only; on web and in demo mode
// none of this renders (every entry point checks isLockCapable()).
//
// State machine (LockGate.mode):
//   boot          opaque cover while the Keychain record is read; guarantees
//                 no frame of user data on cold launch
//   locked        lock screen showing; biometric attempted automatically
//   setup-prompt  one-time offer for existing users who predate the feature
//   open          nothing rendered except the resign-active privacy cover
//
// The privacy cover renders on EVERY resign active regardless of lock state,
// because iOS snapshots the screen for the app switcher at that moment.

import { useState, useEffect, useRef, useCallback } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { reportError, addBreadcrumb } from '../sentry';
import { T } from '../theme';
import SweepReveal from './SweepReveal';
import CircleReveal from './CircleReveal';
import {
  isLockCapable, readLockState, resolveLockStateForUser, setupPin, verifyPin, markPrompted,
  clearLockState, biometricInfo, verifyBiometric, isValidPin,
  noteBackgrounded, graceInfo, clearGrace, startGraceTicker,
  PIN_LENGTH, recordPinLength,
  announceLockPassed,
} from '../lib/appLock';

const FONT = "'DM Sans',sans-serif";
const SERIF = "'DM Serif Display',serif";

// How long to hold the crimson cover after Face ID SUCCESS before opening the
// circle reveal. There is no reliable in-webview signal for the iOS Face ID
// system overlay dismissing (see tryBiometric): this device fires no
// resign/become-active around the prompt, the webview gets no visibility /
// touch / resize event, rAF keeps running unrelated to the overlay, and the
// biometric plugin resolves at the START of the success animation, not its end.
// So this is a tuned fixed hold. Device LOCKSEQ: the overlay was still on
// screen 1.12s after faceid_success, so 600ms was far too short (reveal ran
// under it → hard cut). 1300ms clears it with margin; erring slightly LATE just
// shows the crimson brand cover for an extra beat (seamless), erring early is
// the bug. Paired with the slower 650ms reveal for robustness.
const FACEID_OVERLAY_HOLD_MS = 1300;

// The app underneath the lock (Scorify) raises window.__scorifyAppVisible and
// fires 'scorify-app-visible' once its own crimson boot/data cover is gone and a
// frame has painted. The Face ID circle waits for that so it reveals the real
// app, never crimson-on-crimson. Safety timeout: never let a slow/failed load
// stall the unlock — after it, we reveal anyway (worst case, the old behavior).
const APP_VISIBLE_TIMEOUT_MS = 2500;
const appIsVisible = () => { try { return !!window.__scorifyAppVisible; } catch { return true; } };
const waitForAppVisible = (timeoutMs) => new Promise((resolve) => {
  if (appIsVisible()) { resolve(true); return; }
  let done = false;
  const finish = (visible) => {
    if (done) return; done = true;
    try { window.removeEventListener('scorify-app-visible', onEvt); } catch {}
    clearTimeout(timer);
    resolve(visible);
  };
  const onEvt = () => finish(true);
  try { window.addEventListener('scorify-app-visible', onEvt); } catch {}
  const timer = setTimeout(() => finish(false), timeoutMs);
});

// iOS WKWebView keeps the keyboard up when a focused input unmounts — no
// focusout ever fires (same WebKit behavior viewportReset.js works around).
// So a text field from a previous onboarding step can leave a full QWERTY
// keyboard covering the PIN pad. Every lock surface calls this on mount. The
// PIN pad itself is plain buttons — nothing in these components can summon a
// keyboard once focus is dropped.
const dropKeyboard = () => {
  try {
    const el = document.activeElement;
    if (el && el !== document.body && typeof el.blur === 'function') el.blur();
  } catch {}
};

// ── Small pieces ────────────────────────────────────────────────────────────

const PinDots = ({ count, error, length = PIN_LENGTH }) => (
  <div style={{ display: 'flex', gap: 14, justifyContent: 'center', animation: error ? 'lockShake 0.4s ease' : 'none' }}>
    {Array.from({ length }).map((_, i) => (
      <span key={i} style={{
        width: 12, height: 12, borderRadius: '50%',
        background: i < count ? (error ? T.danger : T.accent) : 'transparent',
        border: `1.5px solid ${i < count ? (error ? T.danger : T.accent) : T.borderStrong}`,
        transition: 'background 0.12s ease, border-color 0.12s ease',
      }} />
    ))}
  </div>
);

const BackspaceIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.text} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6H8l-5 6 5 6h12a1 1 0 0 0 1-1V7a1 1 0 0 0-1-1Z" />
    <path d="m16 9-4.5 4.5M11.5 9 16 13.5" />
  </svg>
);

const FaceIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={T.accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 8V6a2 2 0 0 1 2-2h2M16 4h2a2 2 0 0 1 2 2v2M4 16v2a2 2 0 0 0 2 2h2M16 20h2a2 2 0 0 0 2-2v-2" />
    <path d="M9 10h.01M15 10h.01M9.5 15a3.5 3.5 0 0 0 5 0" />
  </svg>
);

// 3 x 4 grid: digits 1 to 9, then [biometric or empty] 0 [backspace].
// Circular keys, generous touch targets, quiet press state. No sounds, no
// bounce: this should feel like a bank vault door, not a toy.
const PinPad = ({ onDigit, onBackspace, onBiometric, biometricLabel, disabled }) => {
  const key = (content, handler, aria, ghost) => (
    <button
      key={aria}
      onClick={handler}
      disabled={disabled || !handler}
      aria-label={aria}
      style={{
        width: 72, height: 72, borderRadius: '50%',
        background: ghost ? 'transparent' : T.surface,
        border: ghost ? 'none' : `1px solid ${T.border}`,
        color: T.text, fontSize: 26, fontWeight: 500, fontFamily: FONT,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: handler && !disabled ? 'pointer' : 'default',
        opacity: disabled ? 0.4 : 1,
        WebkitTapHighlightColor: 'transparent',
        transition: 'background 0.1s ease',
      }}
      onTouchStart={(e) => { if (!ghost) e.currentTarget.style.background = T.surfaceAlt; }}
      onTouchEnd={(e) => { if (!ghost) e.currentTarget.style.background = T.surface; }}
    >
      {content}
    </button>
  );
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 72px)', gap: 18, justifyContent: 'center' }}>
      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => key(d, () => onDigit(String(d)), `Digit ${d}`))}
      {onBiometric && biometricLabel
        ? key(<FaceIcon />, onBiometric, `Unlock with ${biometricLabel}`, true)
        : <span aria-hidden="true" />}
      {key(0, () => onDigit('0'), 'Digit 0')}
      {key(<BackspaceIcon />, onBackspace, 'Delete digit', true)}
    </div>
  );
};

const lockKeyframes = `
  @keyframes lockShake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-5px)}80%{transform:translateX(5px)}}
`;

// ── Privacy cover ───────────────────────────────────────────────────────────
// Brand screen shown the instant the app resigns active, so the iOS app
// switcher snapshot shows crimson and a wordmark instead of balances.
// zIndex is overridable because the same brand screen doubles as the
// pre-onboarding cover in Scorify.jsx, which must sit BELOW the lock screen
// (1100) and the onboarding sheet (300), while the resign-active cover must
// sit above everything.
export const PrivacyCover = ({ zIndex = 1200 }) => (
  <div style={{
    // accentFill, not accent: the cover matches the NATIVE launch screen,
    // which does not theme — in dark, accent is pink but the cover must stay
    // crimson (Phase 7).
    position: 'fixed', inset: 0, zIndex, background: T.accentFill,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }}>
    <span style={{ fontFamily: SERIF, fontSize: 34, color: '#fff' }}>Scorify</span>
  </div>
);

// ── Lock screen ─────────────────────────────────────────────────────────────

// entrance: 'lift' when a crimson cover is already on screen (cold launch),
// 'rise' when the app itself was visible (resume past grace). onLog is the
// TEMP [LOCKSEQ] beat logger from LockGate; optional everywhere.
// forcePad: start directly on the white PIN pad, skipping the crimson cover
// and the Face ID auto-attempt entirely — the recovery entrance used by the
// watchdogs (2026-09-25 incident: a notification-tap resume relocked but the
// user sat on crimson until force quit). onMounted lets LockGate verify the
// mount actually committed.
export const LockScreen = ({ onUnlocked, onLockedOut, userId, entrance = 'rise', onLog, onMounted, forcePad = false }) => {
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [checking, setChecking] = useState(false);
  const [forgotOpen, setForgotOpen] = useState(false);
  const [bio, setBio] = useState({ available: false, label: null, checked: false });
  const [signingOut, setSigningOut] = useState(false);
  // Two visible phases. 'cover': the crimson Scorify screen, shown while Face
  // ID runs — the happy path lives and dies here and the white lock UI is
  // never rendered. 'pad': the white lock screen with the PIN pad, reached
  // ONLY when Face ID fails, is cancelled, is unavailable, or the user taps
  // Enter PIN. The crimson-to-pad move is a sweep; the crimson-to-app move
  // (Face ID success) is the circle reveal.
  const [phase, setPhase] = useState(forcePad ? 'pad' : 'cover');
  const showPad = phase === 'pad';
  // Ref mirror so beginExit reads the CURRENT phase (a "Use Face ID" retry
  // from the pad must open through the white pad, not crimson).
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  // Set on unlock success; starts the circular exit reveal. The overlay
  // unmounts when the reveal finishes (CircleReveal's onDone → onUnlocked),
  // and goes pointer-transparent the moment it starts, so the app is
  // interactive from the first frame of the exit. exitBg is the colour the
  // circle opens THROUGH: crimson when exiting the cover (Face ID), the app
  // background when exiting the pad (PIN), so the reveal matches whatever was
  // on screen.
  const [exitOrigin, setExitOrigin] = useState(null);
  const [exitBg, setExitBg] = useState(T.accentFill);
  // Reveal duration per path: the Face ID exit opens from the crimson cover and
  // is slowed to 650ms so it reads clearly even if the iOS overlay lingers a
  // touch past the hold; the PIN exit keeps the snappy 450ms.
  const [exitDur, setExitDur] = useState(450);
  const exiting = exitOrigin != null;
  // Stored records decide the dot count and submit trigger: a legacy 6 digit
  // PIN keeps working even though new setups are PIN_LENGTH digits.
  const [pinLen, setPinLen] = useState(PIN_LENGTH);
  const bioTried = useRef(false);
  const padRef = useRef(null);
  const logRef = useRef(onLog);
  logRef.current = onLog;

  // Face ID success opens from screen center (no touch point); a correct PIN
  // opens from the middle of the pad the user was just typing on.
  const beginExit = useCallback((fromPad) => {
    clearGrace();
    let origin = null; // null = screen center (CircleReveal default)
    if (fromPad) {
      try {
        const rect = padRef.current?.getBoundingClientRect();
        if (rect) origin = { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
      } catch {}
    }
    origin = origin || { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    // Open through whatever is on screen: the white pad if we are on it, the
    // crimson cover otherwise — not keyed to Face ID vs PIN.
    const onPad = phaseRef.current === 'pad';
    setExitBg(onPad ? T.bg : T.accentFill);
    setExitDur(onPad ? 450 : 650);
    logRef.current?.('circle_start', { from: fromPad ? 'pad' : 'center', phase: phaseRef.current, dur: onPad ? 450 : 650, x: Math.round(origin.x), y: Math.round(origin.y) });
    setExitOrigin(origin);
  }, []);

  const revealPad = useCallback((why) => {
    logRef.current?.('pad_revealed', { why });
    setPhase('pad');
  }, []);

  const tryBiometric = useCallback(async (label, why) => {
    if (exiting) return;
    logRef.current?.('faceid_requested', { label, why });
    // WATCHDOG B (2026-09-25): verifyIdentity is a native bridge call with no
    // timeout of its own; if it never resolves (a plugin call racing the
    // resume transition), the crimson cover would hang forever. 30s allows a
    // user hesitating at the Face ID prompt; past that, fall to the PIN pad
    // and report. A late success after the timeout resolves into nothing —
    // the user is on the pad and can retry either way.
    const ok = await Promise.race([
      verifyBiometric(label),
      new Promise((r) => setTimeout(() => r('watchdog'), 30000)),
    ]);
    if (ok === 'watchdog') {
      reportError(new Error('AppLock watchdog: Face ID verify never resolved in 30s; fell back to the PIN pad'), { op: 'lock.watchdog', which: 'faceid_verify' });
      logRef.current?.('watchdog_pad', { which: 'faceid_verify' });
      revealPad('watchdog_faceid');
      return;
    }
    logRef.current?.(ok ? 'faceid_success' : 'faceid_failed', { label });
    // Failure or cancel: fall back to the white PIN pad.
    if (!ok) { revealPad('faceid_failed'); return; }
    // Success: the iOS Face ID overlay keeps animating (checkmark → dismiss)
    // for ~1.1s+ past this callback, and this device gives no event when it
    // clears (LOCKSEQ becameActive:false every run). Starting the reveal now
    // plays it under the overlay → hard cut. Hold the crimson cover for a tuned
    // fixed delay past the overlay, then open the circle. The PIN path is
    // unchanged (it never enters this branch).
    logRef.current?.('await_overlay_dismiss', { holdMs: FACEID_OVERLAY_HOLD_MS });
    await new Promise((r) => setTimeout(r, FACEID_OVERLAY_HOLD_MS));
    // Then hold until the app UNDERNEATH the lock has actually painted — its own
    // crimson boot/data cover (Scorify PrivacyCover z290) must be gone, or the
    // circle opens crimson onto crimson (invisible) and the app blinks in when
    // that cover drops (device: identical LOCKSEQ, one animated one blink — the
    // difference was data-load timing). Safety timeout so a slow/failed load
    // never stalls the unlock.
    if (!appIsVisible()) {
      logRef.current?.('await_app_visible');
      const vis = await waitForAppVisible(APP_VISIBLE_TIMEOUT_MS);
      logRef.current?.('app_visible', { visible: vis, timedOut: !vis });
    } else {
      logRef.current?.('app_visible', { visible: true, alreadyVisible: true });
    }
    beginExit(false);
  }, [beginExit, exiting, revealPad]);

  const onMountedRef = useRef(onMounted);
  onMountedRef.current = onMounted;
  useEffect(() => {
    dropKeyboard();
    logRef.current?.('lock_mounted', { entrance });
    logRef.current?.('cover_shown', { via: 'lockscreen' });
    onMountedRef.current?.();
    readLockState(userId).then((s) => { if (s) setPinLen(recordPinLength(s)); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let alive = true;
    biometricInfo().then((info) => { if (alive) setBio({ ...info, checked: true }); });
    return () => { alive = false; };
  }, []);

  // WATCHDOG A (2026-09-25): biometricInfo answers in milliseconds when the
  // bridge is healthy. If it has not answered 4s after mount, the Face ID
  // auto-attempt effect below can never fire and the crimson cover would sit
  // forever with no way out. Treat biometrics as unavailable: sweep to the
  // PIN pad and report. Ref mirrors so the timer reads live values.
  const bioCheckedRef = useRef(false);
  bioCheckedRef.current = bio.checked;
  const exitingRef = useRef(false);
  exitingRef.current = exiting;
  useEffect(() => {
    if (forcePad) return; // already on the pad
    const t = setTimeout(() => {
      if (!bioCheckedRef.current && phaseRef.current === 'cover' && !exitingRef.current) {
        reportError(new Error('AppLock watchdog: biometric availability never answered in 4s; fell back to the PIN pad'), { op: 'lock.watchdog', which: 'bioinfo' });
        logRef.current?.('watchdog_pad', { which: 'bioinfo' });
        revealPad('watchdog_bioinfo');
      }
    }, 4000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Face ID fires immediately, under the crimson cover — no sweep to wait for.
  // If biometrics are unavailable or off, skip straight to the pad (still via
  // the crimson-to-pad sweep). Runs once, when the capability check answers.
  useEffect(() => {
    if (!bio.checked || bioTried.current || phase !== 'cover' || exiting || signingOut || forgotOpen) return;
    bioTried.current = true;
    if (bio.available) tryBiometric(bio.label, 'auto_under_cover');
    else revealPad('unavailable');
  }, [bio, phase, exiting, signingOut, forgotOpen, tryBiometric, revealPad]);

  // Recovery (Forgot PIN + 5-attempt lockout): the PIN now survives a normal
  // sign-out, so recovery must EXPLICITLY clear this user's record first —
  // otherwise signing back in would hit the same (or locked-out) PIN. After the
  // clear, re-auth lands on a fresh setup.
  const resetAndSignOut = async () => {
    try { await clearLockState('forgot_pin', userId); } catch {}
    onLockedOut();
  };

  const submit = async (candidate) => {
    setChecking(true);
    const result = await verifyPin(candidate, userId);
    if (result.ok) { logRef.current?.('pin_success'); beginExit(true); return; }
    setPin('');
    setChecking(false);
    if (result.lockedOut) {
      setSigningOut(true);
      setError('Too many attempts. Signing you out.');
      setTimeout(() => resetAndSignOut(), 1200);
      return;
    }
    setError(result.attemptsLeft === 1
      ? 'Incorrect PIN. 1 attempt left before you are signed out.'
      : `Incorrect PIN. ${result.attemptsLeft} attempts left.`);
  };

  const onDigit = (d) => {
    if (checking || signingOut || exiting) return;
    setError(null);
    const next = (pin + d).slice(0, pinLen);
    setPin(next);
    if (next.length === pinLen) submit(next);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1100, pointerEvents: exiting ? 'none' : 'auto' }}>
      {/* The exit is a circular reveal that opens the CURRENT surface into the
          app: crimson (exitBg) when Face ID succeeds under the cover, the app
          background when a PIN succeeds on the pad. The colour lives on the
          CircleReveal layer so the expanding hole cuts backdrop and content
          together over the already-painted app, no re-render. */}
      <CircleReveal
        playing={exiting}
        origin={exitOrigin}
        duration={exitDur}
        onDone={(info) => { logRef.current?.('circle_end', info || {}); onUnlocked(); }}
        style={{ background: exitBg }}
      >
        {!showPad ? (
          /* HAPPY PATH: the crimson Scorify cover, held while Face ID runs.
             Matches PrivacyCover / the boot cover, so cold launch and resume
             flow into it seamlessly and the white lock screen never appears. */
          <div style={{
            width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: T.accentFill,
          }}>
            <span style={{ fontFamily: SERIF, fontSize: 34, color: '#fff' }}>Scorify</span>
          </div>
        ) : (
          /* FALLBACK: crimson sweeps up to reveal the white PIN pad. 'lift'
             because the crimson cover is always on screen just before this. */
          <SweepReveal
            variant="lift"
            onComplete={() => logRef.current?.('pad_sweep_end')}
          >
            <div style={{
              width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
              alignItems: 'center', justifyContent: 'center', gap: 0, background: T.bg,
              paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            }}>
              <div style={{ fontFamily: SERIF, fontSize: 30, color: T.text, marginBottom: 6 }}>Scorify</div>
              <div style={{ fontFamily: FONT, fontSize: 13, color: T.textMuted, marginBottom: 30 }}>
                {signingOut ? 'For your security, sign in again.' : 'Enter your PIN to unlock'}
              </div>
              <PinDots count={pin.length} error={!!error} length={pinLen} />
              <div style={{
                minHeight: 20, marginTop: 14, marginBottom: 26, fontFamily: FONT,
                fontSize: 12, color: error ? T.danger : 'transparent', textAlign: 'center', padding: '0 32px',
              }}>
                {error || '.'}
              </div>
              <div ref={padRef}>
                <PinPad
                  onDigit={onDigit}
                  onBackspace={() => { if (!checking && !signingOut && !exiting) { setError(null); setPin((p) => p.slice(0, -1)); } }}
                  onBiometric={bio.available ? () => tryBiometric(bio.label, 'manual_pad') : null}
                  biometricLabel={bio.label}
                  disabled={checking || signingOut}
                />
              </div>
              {bio.available && !signingOut && (
                <button
                  onClick={() => tryBiometric(bio.label, 'manual_button')}
                  style={{ marginTop: 26, background: 'transparent', border: 'none', color: T.accent, fontFamily: FONT, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                >
                  Use {bio.label}
                </button>
              )}
              {!signingOut && (
                <button
                  onClick={() => setForgotOpen(true)}
                  style={{ marginTop: bio.available ? 10 : 26, background: 'transparent', border: 'none', color: T.textMuted, fontFamily: FONT, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Forgot PIN?
                </button>
              )}
            </div>
          </SweepReveal>
        )}
      {/* Forgot PIN: same recovery path as the 5 attempt lockout. resetAndSignOut
          clears THIS user's Keychain record (the PIN otherwise survives sign-out),
          then signs out, so the user signs back in and sets a fresh PIN. No data
          is deleted. */}
      {forgotOpen && (
        <div role="dialog" aria-modal="true" aria-label="Reset your PIN" style={{
          position: 'absolute', inset: 0, zIndex: 10, background: T.overlayLight(0.6),
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
        }}>
          <div style={{ width: '100%', maxWidth: 340, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 18, padding: '24px 22px' }}>
            <div style={{ fontFamily: SERIF, fontSize: 21, color: T.text, marginBottom: 10 }}>Reset your PIN</div>
            <div style={{ fontFamily: FONT, fontSize: 13, color: T.textMuted, lineHeight: 1.65, marginBottom: 20 }}>
              To reset your PIN, Scorify signs you out. Sign back in with your account email and password, then choose a new PIN. None of your data is lost.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <button
                onClick={resetAndSignOut}
                style={{ width: '100%', background: T.accentFill, color: '#fff', border: 'none', borderRadius: 10, padding: '12px', fontSize: 14, fontWeight: 700, fontFamily: FONT, cursor: 'pointer' }}
              >
                Sign out and reset PIN
              </button>
              <button
                onClick={() => setForgotOpen(false)}
                style={{ width: '100%', background: 'transparent', color: T.textMuted, border: `1px solid ${T.border}`, borderRadius: 10, padding: '11px', fontSize: 13, fontFamily: FONT, cursor: 'pointer' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      </CircleReveal>
      <style>{lockKeyframes}</style>
    </div>
  );
};

// ── PIN setup ───────────────────────────────────────────────────────────────
// Two passes: enter, then confirm. Used by onboarding, the one-time prompt
// for existing users, and the Profile modal (enable or change PIN). Renders
// inline in whatever container the caller provides.

// ownerId: the signed-in Supabase user id — stamped into the Keychain record
// so it can never operate for a different account on this device.
export const LockSetup = ({ onDone, onSkip, skipLabel, ownerId }) => {
  const [stage, setStage] = useState('enter'); // enter | confirm | saving
  const [first, setFirst] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState(null);
  const [bio, setBio] = useState({ available: false, label: null });

  useEffect(() => { dropKeyboard(); biometricInfo().then(setBio); }, []);

  const onDigit = async (d) => {
    if (stage === 'saving') return;
    setError(null);
    const next = (pin + d).slice(0, PIN_LENGTH);
    setPin(next);
    if (next.length < PIN_LENGTH) return;
    if (stage === 'enter') {
      setFirst(next);
      setPin('');
      setStage('confirm');
      return;
    }
    if (next !== first) {
      setPin('');
      setFirst('');
      setStage('enter');
      setError('The PINs did not match. Start again.');
      return;
    }
    setStage('saving');
    const ok = await setupPin(next, ownerId);
    if (!ok || !isValidPin(next)) {
      setPin(''); setFirst(''); setStage('enter');
      setError('Could not save your PIN. Please try again.');
      return;
    }
    onDone();
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <div style={{ fontFamily: FONT, fontSize: 13, color: T.textMuted, marginBottom: 6, textAlign: 'center' }}>
        {stage === 'confirm' ? 'Enter the same PIN again to confirm' : `Choose a ${PIN_LENGTH} digit PIN`}
      </div>
      <div style={{ fontFamily: FONT, fontSize: 12, color: T.textDim, marginBottom: 22, textAlign: 'center', lineHeight: 1.5 }}>
        {bio.available
          ? `${bio.label} unlocks Scorify. The PIN is the backup if ${bio.label} is unavailable.`
          : 'Scorify will ask for this PIN when you return to the app.'}
      </div>
      <PinDots count={pin.length} error={!!error} />
      <div style={{ minHeight: 18, marginTop: 12, marginBottom: 22, fontFamily: FONT, fontSize: 12, color: error ? T.danger : 'transparent', textAlign: 'center' }}>
        {error || '.'}
      </div>
      <PinPad
        onDigit={onDigit}
        onBackspace={() => { setError(null); setPin((p) => p.slice(0, -1)); }}
        disabled={stage === 'saving'}
      />
      {onSkip && (
        <button
          onClick={onSkip}
          style={{ marginTop: 24, background: 'transparent', border: 'none', color: T.textMuted, fontFamily: FONT, fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}
        >
          {skipLabel || 'Skip for now'}
        </button>
      )}
      <style>{lockKeyframes}</style>
    </div>
  );
};

// ── One-time setup prompt for existing users ────────────────────────────────

const SetupPromptSheet = ({ onDone, onSkip, ownerId }) => (
  <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: T.bg, overflowY: 'auto' }}>
    <div style={{
      minHeight: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      padding: 'calc(env(safe-area-inset-top, 0px) + 32px) 28px calc(env(safe-area-inset-bottom, 0px) + 32px)',
    }}>
      <div style={{ fontFamily: SERIF, fontSize: 26, color: T.text, marginBottom: 10, textAlign: 'center', lineHeight: 1.25 }}>
        Lock Scorify when you step away.
      </div>
      <div style={{ fontFamily: FONT, fontSize: 13, color: T.textMuted, marginBottom: 28, textAlign: 'center', lineHeight: 1.65, maxWidth: 340 }}>
        Your balances, health data and journal live here. Add a PIN and Scorify will ask for Face ID or the PIN whenever you come back. You can change this anytime in Profile.
      </div>
      <LockSetup onDone={onDone} onSkip={onSkip} skipLabel="Not now" ownerId={ownerId} />
    </div>
  </div>
);

// ── LockGate ────────────────────────────────────────────────────────────────

const LOCK_PROMPT_LS = 'scorify_lock_prompt_done';

// Fired by anything that flips the lock on or off outside LockGate (the
// Profile modal, the onboarding step), so the gate's relock decision on the
// next resume matches the new setting without a cold launch.
export const LOCK_CHANGED_EVENT = 'scorify-lock-changed';
export const announceLockChanged = () => { try { window.dispatchEvent(new Event(LOCK_CHANGED_EVENT)); } catch {} };

export const LockGate = ({ user, onSignOut }) => {
  const active = isLockCapable() && !!user;
  const [mode, setMode] = useState(active ? 'boot' : 'open');
  // Past the lock? Published for work that must wait for it (push action
  // buttons, src/lib/pushActions.js). Not active (web, demo, no user) is open.
  useEffect(() => { announceLockPassed(!active || mode === 'open'); }, [active, mode]);
  const [covered, setCovered] = useState(false);
  // 'lift' when the lock follows a crimson cover (cold launch: the sweep
  // layer starts covering and lifts off); 'rise' when the app was visible
  // (resume past grace: the sweep climbs in from the bottom first).
  const [entrance, setEntrance] = useState('lift');
  const enabledRef = useRef(false);
  // Mirror for the appStateChange handler, which must read the CURRENT mode
  // (the listener closes over the mount-time value otherwise).
  const modeRef = useRef(mode);
  modeRef.current = mode;
  // A global flag the App.js theme watcher reads so a theme reload never fires
  // while the lock or a cover is on screen (it would interrupt PIN entry or
  // flash past the lock). True whenever anything but the open app is showing.
  useEffect(() => { try { window.__scorifyLockActive = (mode !== 'open') || covered; } catch {} }, [mode, covered]);
  // WATCHDOG plumbing (2026-09-25 incident: a notification-tap resume logged
  // relock_resume but the lock screen never mounted — the user sat on the
  // crimson cover until force quit). lockNonce remounts LockScreen fresh;
  // forcePad skips the cover + Face ID auto-attempt on recovery entrances so
  // the PIN pad is guaranteed visible.
  const [lockNonce, setLockNonce] = useState(0);
  const [forcePad, setForcePad] = useState(false);
  const lockScreenMountedRef = useRef(false);

  // [LOCKSEQ] — unlock-sequence timing beats as Sentry BREADCRUMBS (2026-09-29,
  // was a Supabase debug_log write of user_id + timings, stripped pre-TestFlight).
  // Non-PII primitives only (ms + status flags); they ride attached to whatever
  // error fires next, so a future SCORIFY-APP-F Face-ID-hang event shows the
  // beat trail (faceid_requested -> appstate -> watchdog_pad). ms is relative to
  // LockGate mount.
  const t0 = useRef(performance.now());
  const logBeat = useCallback((message, payload) => {
    try {
      addBreadcrumb('lock', message, { ms: Math.round(performance.now() - t0.current), ...(payload || {}) });
    } catch {}
  }, []);

  // Cold launch resolution. `boot` renders the opaque cover, so no user data
  // can paint before the Keychain answers.
  useEffect(() => {
    if (!active) { setMode('open'); return; }
    logBeat('cover_shown', { mode: 'boot' });
    let alive = true;
    (async () => {
      // resolveLockStateForUser layers the two anti-bleed checks: the fresh
      // install marker (inside readLockState) and account ownership. A stale
      // record from a reinstall or another account comes back as null, so
      // this user simply gets the normal setup offer.
      const state = await resolveLockStateForUser(user.id, (reason) => logBeat('stale_record_cleared', { reason }));
      if (!alive) return;
      enabledRef.current = !!state?.enabled;
      if (state?.enabled) { setEntrance('lift'); logBeat('lock_decided', { entrance: 'lift' }); setMode('locked'); return; }
      // One-time offer for accounts that predate the lock. New signups skip
      // it here because onboarding owns the same step (Auth.jsx sets the
      // flag on signup; the Keychain record exists after onboarding).
      let promptDone = false;
      try { promptDone = !!localStorage.getItem(LOCK_PROMPT_LS); } catch {}
      if (!state?.prompted && !promptDone) { setMode('setup-prompt'); return; }
      setMode('open');
    })();
    return () => { alive = false; };
  }, [active, user?.id]);

  // Keep enabledRef honest when the Profile modal or onboarding changes the
  // lock while the app is open.
  useEffect(() => {
    if (!active) return;
    const refresh = () => { readLockState(user?.id).then((s) => { enabledRef.current = !!s?.enabled; }); };
    window.addEventListener(LOCK_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(LOCK_CHANGED_EVENT, refresh);
  }, [active]);

  // Background / resume: cover instantly on resign active (app switcher
  // snapshot), and on resume compare timestamps for the grace period.
  useEffect(() => {
    if (!active) return;
    // Heartbeat that measures real JS suspension time — the half of the
    // grace decision that queued app-state events cannot spoof.
    startGraceTicker();
    let sub;
    (async () => {
      sub = await CapacitorApp.addListener('appStateChange', ({ isActive }) => {
        // Breadcrumb EVERY app-state flip, including while the lock is up, so a
        // future SCORIFY-APP-F event shows whether the app backgrounded while
        // Face ID was pending (isActive:false between faceid_requested and the
        // watchdog). Non-PII: a boolean and the mode string.
        addBreadcrumb('lock', 'appstate', { isActive, mode: modeRef.current });
        // While the lock screen (or boot cover) is up, the screen already
        // shows no user data, so the privacy cover has no job. Critically,
        // the Face ID system prompt fires its OWN resign/become-active
        // cycle: reacting to it stacked the privacy cover (z 1200) over the
        // lock screen (z 1100), which is exactly why the circle reveal ran
        // invisibly under crimson on device (LOCKSEQ 19:47: circle_start to
        // circle_end 460ms, yet the user saw a hard cut). Grace tracking
        // only means anything from the unlocked app anyway.
        if (modeRef.current !== 'open') {
          logBeat('appstate_ignored', { isActive, mode: modeRef.current });
          return;
        }
        if (!isActive) {
          noteBackgrounded();
          setCovered(true);
          logBeat('bg_noted');
          return;
        }
        setCovered(false);
        // graceInfo carries BOTH signals: sinceBg (the resign timestamp,
        // which screen auto-lock can deliver late enough to read ~0) and the
        // heartbeat's suspension gap, which late delivery cannot forge. The
        // full math is logged so device passes read the decision directly.
        const info = graceInfo();
        const relock = enabledRef.current && info.expired;
        logBeat('resume_check', { sinceBg: info.sinceBg, rawTickGap: info.rawTickGap, recentSuspension: info.recentSuspension, relock });
        if (relock) {
          // 'lift', not 'rise': the privacy cover (crimson) is what is on
          // screen at this moment, so the sweep must START covering and
          // lift off. 'rise' began below the screen and exposed the lock
          // UI for a beat between cover-drop and the sweep arriving
          // (device sequence: crimson, PIN screen, crimson again).
          setEntrance('lift');
          logBeat('relock_resume', { entrance: 'lift' });
          setMode('locked');
          // RE-ASSERT (2026-09-25): if this state update is lost (the
          // incident's signature — relock_resume logged, lock never mounted,
          // crimson forever), modeRef still reads 'open' 1.5s later. Re-issue
          // the lock, this time straight onto the PIN pad, and report.
          setTimeout(() => {
            if (modeRef.current === 'open') {
              reportError(new Error('AppLock watchdog: relock state update did not take after resume; re-asserting with the PIN pad'), { op: 'lock.watchdog', which: 'relock_reassert' });
              logBeat('relock_reassert');
              setCovered(false);
              setForcePad(true);
              setEntrance('lift');
              setLockNonce((n) => n + 1);
              setMode('locked');
            }
          }, 1500);
        }
        clearGrace();
      });
    })();
    return () => { try { sub?.remove(); } catch {} };
  }, [active]);

  // MOUNT WATCHDOG (2026-09-25): entering 'locked' must produce a mounted
  // LockScreen within 4s (onMounted flips the ref). If it does not — a lost
  // commit, a crash torn down inside the subtree — remount it fresh, straight
  // onto the PIN pad, and report. The crimson cover must never be a dead end.
  useEffect(() => {
    // Reset the mount flag when LEAVING 'locked', never on entering it: child
    // effects run before parent effects, so on the commit that both flips mode
    // to 'locked' and mounts LockScreen, onMounted has ALREADY set the ref by
    // the time this body runs — resetting it here erased the confirmation and
    // made the watchdog fire on every launch still on the lock at +4s
    // (SCORIFY-APP-D false alarm, diagnosed 2026-09-27). Resetting on the
    // not-locked path keeps the real coverage: a torn-down or never-mounted
    // LockScreen never runs its mount effect, so the ref stays false from the
    // last unlock; a wholly lost commit is the relock re-assert's job (1.5s).
    if (mode !== 'locked') { lockScreenMountedRef.current = false; return; }
    const t = setTimeout(() => {
      if (!lockScreenMountedRef.current) {
        reportError(new Error('AppLock watchdog: LockScreen did not mount within 4s of locking; remounting with the PIN pad'), { op: 'lock.watchdog', which: 'mount' });
        logBeat('lock_mount_watchdog');
        setCovered(false);
        setForcePad(true);
        setLockNonce((n) => n + 1);
      }
    }, 4000);
    return () => clearTimeout(t);
  }, [mode, lockNonce, logBeat]);

  // BOOT WATCHDOG (2026-09-25): the boot cover waits on a Keychain read; if
  // that native call hangs, cold launch is crimson forever. Past 8s, fail
  // CLOSED to the lock screen's PIN pad (the pad carries the forgot-PIN /
  // sign-out escape, which a dead cover does not) and report.
  useEffect(() => {
    if (mode !== 'boot') return;
    const t = setTimeout(() => {
      reportError(new Error('AppLock watchdog: lock-state read hung 8s at boot; failing closed to the PIN pad'), { op: 'lock.watchdog', which: 'boot' });
      logBeat('boot_watchdog');
      enabledRef.current = true; // fail closed — unknown state must not fail open
      setForcePad(true);
      setEntrance('lift');
      setMode('locked');
    }, 8000);
    return () => clearTimeout(t);
  }, [mode, logBeat]);

  if (!active) return null;

  const rememberPrompted = () => { try { localStorage.setItem(LOCK_PROMPT_LS, '1'); } catch {} };

  return (
    <>
      {/* Boot cover is PLAIN crimson on purpose: the native launch screen,
          the webview background and the sweep layer are all the same flat
          T.accent, so cold launch reads as one unbroken surface until the
          sweep lifts. No wordmark here — it would pop out at the swap. */}
      {mode === 'boot' && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, background: T.accentFill }} />
      )}
      {mode === 'locked' && (
        <LockScreen
          key={lockNonce}
          onUnlocked={() => { enabledRef.current = true; setForcePad(false); setMode('open'); }}
          onLockedOut={onSignOut}
          userId={user?.id}
          entrance={entrance}
          onLog={logBeat}
          onMounted={() => { lockScreenMountedRef.current = true; }}
          forcePad={forcePad}
        />
      )}
      {mode === 'setup-prompt' && (
        <SetupPromptSheet
          ownerId={user.id}
          onDone={() => { enabledRef.current = true; rememberPrompted(); announceLockChanged(); setMode('open'); }}
          onSkip={async () => { await markPrompted(user.id); rememberPrompted(); announceLockChanged(); setMode('open'); }}
        />
      )}
      {covered && <PrivacyCover />}
    </>
  );
};

export default LockGate;
